import React, { useState, useEffect } from 'react';
import { subcontractorInvoicesAPI, usersAPI, timesheetsAPI, customEntriesAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  DollarSign,
  CheckCircle2,
  Clock,
  Edit2,
  Trash2,
  X,
  Save,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const StatusBadge = ({ status }) => {
  const config = {
    draft: { className: 'bg-slate-100 text-slate-700', label: 'Unpaid' },
    sent: { className: 'bg-amber-100 text-amber-700', label: 'Pending' },
    paid: { className: 'bg-emerald-100 text-emerald-700', label: 'Paid' },
    overdue: { className: 'bg-red-100 text-red-700', label: 'Overdue' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.className}`}>
      {c.label}
    </span>
  );
};

const DifferenceDisplay = ({ estimated, actual }) => {
  // Difference = Estimated - Actual
  // Positive (green) = paid LESS than expected (good)
  // Negative (red) = paid MORE than expected (bad/cost overrun)
  const diff = estimated - actual;
  if (Math.abs(diff) < 0.01) {
    return (
      <span className="flex items-center gap-1 text-slate-500 text-xs">
        <Minus className="w-3 h-3" /> $0.00
      </span>
    );
  }
  if (diff > 0) {
    // Paid less than estimated - GOOD (green)
    return (
      <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
        <TrendingUp className="w-3 h-3" /> +${diff.toFixed(2)}
      </span>
    );
  }
  // Paid more than estimated - BAD (red)
  return (
    <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
      <TrendingDown className="w-3 h-3" /> -${Math.abs(diff).toFixed(2)}
    </span>
  );
};

const PaymentModal = ({ isOpen, onClose, onSave, payment, users, saving, allPayments }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    subtotal: '',
    gst: '',
    total: '',
    description: '',
    status: 'draft',
    paid_date: '',
    payment_reference: '',
    timesheet_ids: [],
    custom_entry_ids: [],
  });
  const [availableTimesheets, setAvailableTimesheets] = useState([]);
  const [availableCustomEntries, setAvailableCustomEntries] = useState([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [selectedCustomEntries, setSelectedCustomEntries] = useState([]);
  const [estimatedPay, setEstimatedPay] = useState(0);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [periodFilter, setPeriodFilter] = useState({ start: '', end: '' });

  useEffect(() => {
    if (payment) {
      setFormData({
        user_id: payment.user_id || '',
        invoice_number: payment.invoice_number || '',
        invoice_date: payment.invoice_date || '',
        subtotal: payment.subtotal || '',
        gst: payment.gst || '',
        total: payment.total || '',
        description: payment.description || '',
        status: payment.status || 'draft',
        paid_date: payment.paid_date || '',
        payment_reference: payment.payment_reference || '',
        timesheet_ids: payment.timesheet_ids || [],
        custom_entry_ids: payment.custom_entry_ids || [],
      });
      setSelectedTimesheets(payment.timesheet_ids || []);
      setSelectedCustomEntries(payment.custom_entry_ids || []);
    } else {
      // Reset form to initial state when no payment is being edited
      setFormData({
        user_id: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        subtotal: '',
        gst: '',
        total: '',
        description: '',
        status: 'draft',
        paid_date: '',
        payment_reference: '',
        timesheet_ids: [],
        custom_entry_ids: [],
      });
      setSelectedTimesheets([]);
      setSelectedCustomEntries([]);
      setAvailableTimesheets([]);
      setAvailableCustomEntries([]);
      setEstimatedPay(0);
      setPeriodFilter({ start: '', end: '' });
    }
  }, [payment, isOpen]); // Added isOpen to dependency array to reset form when modal opens

  // Load timesheets and custom entries when user is selected
  useEffect(() => {
    const loadTimesheetsAndEntries = async () => {
      if (!formData.user_id) {
        setAvailableTimesheets([]);
        setAvailableCustomEntries([]);
        return;
      }
      
      setLoadingTimesheets(true);
      try {
        // Load timesheets
        const res = await timesheetsAPI.getApprovedByUser(formData.user_id);
        const userTimesheets = res.data;
        
        // Load custom entries for this user
        const customRes = await customEntriesAPI.getAll();
        const userCustomEntries = customRes.data.filter(ce => ce.user_id === formData.user_id);
        
        // Get IDs of timesheets already linked to other payments (excluding current payment being edited)
        const linkedTsIds = new Set();
        const linkedCeIds = new Set();
        allPayments.forEach(p => {
          if (payment && p.id === payment.id) return; // Skip current payment
          (p.timesheet_ids || []).forEach(tid => linkedTsIds.add(tid));
          (p.custom_entry_ids || []).forEach(cid => linkedCeIds.add(cid));
        });
        
        // Filter to only show unlinked items OR items linked to current payment
        const availableTs = userTimesheets.filter(ts => 
          !linkedTsIds.has(ts.id) || (payment && payment.timesheet_ids?.includes(ts.id))
        );
        const availableCe = userCustomEntries.filter(ce => 
          !linkedCeIds.has(ce.id) || (payment && payment.custom_entry_ids?.includes(ce.id))
        );
        
        setAvailableTimesheets(availableTs);
        setAvailableCustomEntries(availableCe);
        
        // If editing, restore selected items
        if (payment) {
          if (payment.timesheet_ids) setSelectedTimesheets(payment.timesheet_ids);
          if (payment.custom_entry_ids) setSelectedCustomEntries(payment.custom_entry_ids);
        }
      } catch (error) {
        console.error('Failed to load timesheets and custom entries:', error);
      } finally {
        setLoadingTimesheets(false);
      }
    };
    
    loadTimesheetsAndEntries();
  }, [formData.user_id, payment, allPayments]);

  // Calculate estimated pay when selected timesheets/custom entries change
  useEffect(() => {
    const calculateEstimate = async () => {
      if (selectedTimesheets.length === 0 && selectedCustomEntries.length === 0) {
        setEstimatedPay(0);
        return;
      }
      
      try {
        const res = await timesheetsAPI.estimatePay(selectedTimesheets, selectedCustomEntries);
        setEstimatedPay(res.data.total_estimated_pay);
      } catch (error) {
        console.error('Failed to calculate estimate:', error);
      }
    };
    
    calculateEstimate();
  }, [selectedTimesheets, selectedCustomEntries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate GST (10%) and total when subtotal changes
      if (name === 'subtotal') {
        const subtotal = parseFloat(value) || 0;
        const gst = subtotal * 0.10; // 10% GST
        updated.gst = gst.toFixed(2);
        updated.total = (subtotal + gst).toFixed(2);
      }
      
      // Recalculate total when GST is manually changed
      if (name === 'gst') {
        const subtotal = parseFloat(prev.subtotal) || 0;
        const gst = parseFloat(value) || 0;
        updated.total = (subtotal + gst).toFixed(2);
      }
      
      // Reset timesheets and custom entries when user changes
      if (name === 'user_id') {
        updated.timesheet_ids = [];
        updated.custom_entry_ids = [];
        setSelectedTimesheets([]);
        setSelectedCustomEntries([]);
        setEstimatedPay(0);
        setPeriodFilter({ start: '', end: '' });
      }
      
      return updated;
    });
  };

  const handleTimesheetToggle = (timesheetId) => {
    setSelectedTimesheets(prev => {
      if (prev.includes(timesheetId)) {
        return prev.filter(id => id !== timesheetId);
      } else {
        return [...prev, timesheetId];
      }
    });
  };

  const handleCustomEntryToggle = (entryId) => {
    setSelectedCustomEntries(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId);
      } else {
        return [...prev, entryId];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      subtotal: parseFloat(formData.subtotal) || 0,
      gst: parseFloat(formData.gst) || 0,
      total: parseFloat(formData.total) || 0,
      timesheet_ids: selectedTimesheets,
      custom_entry_ids: selectedCustomEntries,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-slate-900">
            {payment ? 'Edit Payment Record' : 'Add Payment Record'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Subcontractor Select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Subcontractor *
            </label>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              required
              disabled={!!payment}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent disabled:bg-slate-100"
              data-testid="payment-user-select"
            >
              <option value="">Select a subcontractor...</option>
              {users.filter(u => u.role === 'subcontractor').sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Timesheets Selection */}
          {formData.user_id && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Link Timesheets (Optional)
              </label>
              
              {/* Period Filter */}
              {availableTimesheets.length > 0 && (
                <div className="flex gap-3 mb-2">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">From</label>
                    <input
                      type="date"
                      value={periodFilter.start}
                      onChange={(e) => setPeriodFilter(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2] focus:border-transparent"
                      data-testid="period-filter-start"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">To</label>
                    <input
                      type="date"
                      value={periodFilter.end}
                      onChange={(e) => setPeriodFilter(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-[#194eb2] focus:border-transparent"
                      data-testid="period-filter-end"
                    />
                  </div>
                  {(periodFilter.start || periodFilter.end) && (
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => setPeriodFilter({ start: '', end: '' })}
                        className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded hover:bg-slate-50"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {loadingTimesheets ? (
                <div className="flex items-center gap-2 text-slate-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading timesheets...
                </div>
              ) : availableTimesheets.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">No approved timesheets available for this subcontractor</p>
              ) : (
                <>
                  {/* Filtered Timesheets List */}
                  {(() => {
                    const filteredTimesheets = availableTimesheets.filter(ts => {
                      if (periodFilter.start && ts.period_end < periodFilter.start) return false;
                      if (periodFilter.end && ts.period_start > periodFilter.end) return false;
                      return true;
                    });
                    
                    return filteredTimesheets.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2 border border-slate-200 rounded-lg p-3">
                        No timesheets match the selected period filter
                      </p>
                    ) : (
                      <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                        {filteredTimesheets.map(ts => (
                          <label
                            key={ts.id}
                            className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                              selectedTimesheets.includes(ts.id) ? 'bg-blue-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTimesheets.includes(ts.id)}
                              onChange={() => handleTimesheetToggle(ts.id)}
                              className="w-4 h-4 text-[#194eb2] rounded border-slate-300 focus:ring-[#194eb2]"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">
                                {formatDateRange(ts.period_start, ts.period_end)}
                              </p>
                              <p className="text-xs text-slate-500">
                                {ts.total_days > 0 && `${ts.total_days} days`}
                                {ts.total_days > 0 && ts.total_hours > 0 && ', '}
                                {ts.total_hours > 0 && `${ts.total_hours} hours`}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
              
              {/* Estimated Pay Display */}
              {(selectedTimesheets.length > 0 || selectedCustomEntries.length > 0) && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700">
                      Estimated Pay ({selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''}{selectedCustomEntries.length > 0 ? `, ${selectedCustomEntries.length} custom entr${selectedCustomEntries.length !== 1 ? 'ies' : 'y'}` : ''})
                    </span>
                    <span className="text-lg font-bold text-blue-800">
                      ${estimatedPay.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">Based on resource pay rates + custom entry values</p>
                </div>
              )}
              
              {/* Custom Entries Section */}
              {availableCustomEntries.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Custom Entries
                  </label>
                  <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto">
                    {availableCustomEntries.map(ce => (
                      <label
                        key={ce.id}
                        className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                          selectedCustomEntries.includes(ce.id) ? 'bg-amber-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCustomEntries.includes(ce.id)}
                          onChange={() => handleCustomEntryToggle(ce.id)}
                          className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">
                            {formatDateRange(ce.period_start, ce.period_end)}
                            <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Custom</span>
                          </p>
                          <p className="text-xs text-slate-500">
                            {ce.entries.length} entr{ce.entries.length !== 1 ? 'ies' : 'y'} • ${ce.total_value.toFixed(2)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Invoice Number *
            </label>
            <input
              type="text"
              name="invoice_number"
              value={formData.invoice_number}
              onChange={handleChange}
              required
              placeholder="INV-001"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              data-testid="payment-invoice-number"
            />
          </div>

          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Invoice Date *
            </label>
            <input
              type="date"
              name="invoice_date"
              value={formData.invoice_date}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              data-testid="payment-invoice-date"
            />
          </div>

          {/* Amount Fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subtotal *
              </label>
              <input
                type="number"
                step="0.01"
                name="subtotal"
                value={formData.subtotal}
                onChange={handleChange}
                required
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
                data-testid="payment-subtotal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                GST
              </label>
              <input
                type="number"
                step="0.01"
                name="gst"
                value={formData.gst}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
                data-testid="payment-gst"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Total
              </label>
              <input
                type="number"
                step="0.01"
                name="total"
                value={formData.total}
                readOnly
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
                data-testid="payment-total"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description / Notes
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              data-testid="payment-description"
            />
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Payment Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
              data-testid="payment-status"
            >
              <option value="draft">Unpaid</option>
              <option value="sent">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          {/* Payment Date (shown if status is paid) */}
          {formData.status === 'paid' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Date
              </label>
              <input
                type="date"
                name="paid_date"
                value={formData.paid_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
                data-testid="payment-paid-date"
              />
            </div>
          )}

          {/* Payment Reference */}
          {formData.status === 'paid' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Reference
              </label>
              <input
                type="text"
                name="payment_reference"
                value={formData.payment_reference}
                onChange={handleChange}
                placeholder="Bank ref, check number, etc."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
                data-testid="payment-reference"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 text-white bg-[#194eb2] rounded-lg hover:bg-[#153d8a] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="payment-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {payment ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SubcontractorPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [estimatedPayMap, setEstimatedPayMap] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, usersRes] = await Promise.all([
        subcontractorInvoicesAPI.getAll(),
        usersAPI.getAll()
      ]);
      setPayments(paymentsRes.data);
      setUsers(usersRes.data);
      
      // Calculate estimated pay for each payment with linked timesheets/custom entries
      const estimateMap = {};
      for (const payment of paymentsRes.data) {
        const hasTsIds = payment.timesheet_ids && payment.timesheet_ids.length > 0;
        const hasCeIds = payment.custom_entry_ids && payment.custom_entry_ids.length > 0;
        
        if (hasTsIds || hasCeIds) {
          try {
            const estRes = await timesheetsAPI.estimatePay(
              payment.timesheet_ids || [],
              payment.custom_entry_ids || []
            );
            estimateMap[payment.id] = estRes.data.total_estimated_pay;
          } catch (e) {
            estimateMap[payment.id] = 0;
          }
        } else {
          estimateMap[payment.id] = 0;
        }
      }
      setEstimatedPayMap(estimateMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingPayment) {
        await subcontractorInvoicesAPI.update(editingPayment.id, formData);
        toast.success('Payment record updated');
      } else {
        await subcontractorInvoicesAPI.create(formData);
        toast.success('Payment record created');
      }
      setShowModal(false);
      setEditingPayment(null);
      await fetchData();
    } catch (error) {
      console.error('Failed to save payment:', error);
      toast.error('Failed to save payment record');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setShowModal(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    
    try {
      await subcontractorInvoicesAPI.delete(paymentId);
      toast.success('Payment record deleted');
      await fetchData();
    } catch (error) {
      console.error('Failed to delete payment:', error);
      toast.error('Cannot delete non-draft payment records');
    }
  };

  const handleQuickMarkPaid = async (payment) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await subcontractorInvoicesAPI.update(payment.id, {
        status: 'paid',
        paid_date: today
      });
      toast.success(`Marked ${payment.invoice_number} as paid`);
      await fetchData();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      toast.error('Failed to update payment status');
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const filteredPayments = filterUser 
    ? payments.filter(p => p.user_id === filterUser)
    : payments;

  // Calculate summary stats
  const stats = {
    total: filteredPayments.reduce((sum, p) => sum + (p.total || 0), 0),
    paid: filteredPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.total || 0), 0),
    unpaid: filteredPayments.filter(p => p.status !== 'paid').reduce((sum, p) => sum + (p.total || 0), 0),
    count: filteredPayments.length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in" data-testid="subcontractor-payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Subcontractor Payments</h1>
            <p className="mt-1 text-slate-500">Track payments made to subcontractors</p>
          </div>
          <button
            onClick={() => { 
              setEditingPayment(null); 
              // Force a brief close/open cycle to ensure form resets completely
              setShowModal(false);
              setTimeout(() => setShowModal(true), 10);
            }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-payment-btn"
          >
            <Plus className="w-4 h-4" />
            Add Payment Record
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Invoiced</p>
                <p className="text-xl font-bold text-slate-900">${stats.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Paid</p>
                <p className="text-xl font-bold text-emerald-600">${stats.paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-xl font-bold text-amber-600">${stats.unpaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#194eb2]/10 rounded-lg">
                <DollarSign className="w-5 h-5 text-[#194eb2]" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Records</p>
                <p className="text-xl font-bold text-slate-900">{stats.count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="card-base">
          <div className="p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">Filter by Subcontractor:</label>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#194eb2] focus:border-transparent"
                data-testid="filter-user-select"
              >
                <option value="">All Subcontractors</option>
                {users.filter(u => u.role === 'subcontractor').sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left">Subcontractor</th>
                  <th className="px-6 py-3 text-left">Invoice #</th>
                  <th className="px-6 py-3 text-left">Date</th>
                  <th className="px-6 py-3 text-right">Estimated (Ex GST)</th>
                  <th className="px-6 py-3 text-right">Actual (Ex GST)</th>
                  <th className="px-6 py-3 text-right">Diff</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-left">Paid Date</th>
                  <th className="px-6 py-3 text-center">Linked</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-slate-500">
                      No payment records found. Click "Add Payment Record" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((payment) => {
                    const estimated = estimatedPayMap[payment.id] || 0;
                    // Use subtotal (ex-GST) for comparison with estimated (which is also ex-GST)
                    const actual = payment.subtotal || 0;
                    
                    return (
                      <tr key={payment.id} className="table-row" data-testid={`payment-row-${payment.id}`}>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                          {getUserName(payment.user_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                          {payment.invoice_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {formatDate(payment.invoice_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 text-right font-mono">
                          {estimated > 0 ? `$${estimated.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900 text-right font-mono">
                          ${actual.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {estimated > 0 ? (
                            <DifferenceDisplay estimated={estimated} actual={actual} />
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <StatusBadge status={payment.status} />
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {payment.paid_date ? formatDate(payment.paid_date) : '-'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              payment.timesheet_ids?.length > 0 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                              {payment.timesheet_ids?.length || 0} TS
                            </span>
                            {payment.custom_entry_ids?.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                                {payment.custom_entry_ids.length} CE
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {payment.status !== 'paid' && (
                              <button
                                onClick={() => handleQuickMarkPaid(payment)}
                                className="px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors flex items-center gap-1"
                                title="Mark as Paid"
                                data-testid={`mark-paid-${payment.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                Mark Paid
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(payment)}
                              className="p-1.5 text-slate-400 hover:text-[#194eb2] hover:bg-slate-100 rounded-lg transition-colors"
                              title="Edit"
                              data-testid={`edit-payment-${payment.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                              data-testid={`delete-payment-${payment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        <PaymentModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingPayment(null); }}
          onSave={handleSave}
          payment={editingPayment}
          users={users}
          saving={saving}
          allPayments={payments}
        />
      </div>
    </DashboardLayout>
  );
};

export default SubcontractorPaymentsPage;
